import { useEffect, useRef, useState } from "react";

type SearchableReactDropdownProps<T> = {
  options: readonly T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string;
  value?: string;
  onChange: (option: T) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noOptionsMessage?: string;
  controlClassName?: string;
  menuClassName?: string;
  arrowClassName?: string;
  actionButton?: {
    label: string;
    onClick: () => void;
  };
};

const SearchableReactDropdown = <T,>({
  options,
  getOptionLabel,
  getOptionValue,
  value,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  noOptionsMessage = "No options found",
  controlClassName = "border rounded text-lg cursor-pointer w-full",
  menuClassName = "border rounded cursor-pointer",
  arrowClassName,
  actionButton,
}: SearchableReactDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const controlRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(
    (option) => getOptionValue(option) === value,
  );
  const filteredOptions = options.filter((option) =>
    getOptionLabel(option)
      .toLocaleLowerCase()
      .includes(searchTerm.toLocaleLowerCase()),
  );

  useEffect(() => {
    if (!isOpen) return;

    searchInputRef.current?.focus();

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        !dropdownRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  const toggleDropdown = () => {
    if (isOpen) setSearchTerm("");
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div ref={dropdownRef} className={`relative ${isOpen ? "is-open" : ""}`}>
      <button
        ref={controlRef}
        type="button"
        className={`flex w-full items-center text-left ${controlClassName}`}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="p-2">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <div className="flex items-center align-items">
          <span
            className={`ml-auto pl-3 ${arrowClassName ?? ""}`}
            aria-hidden="true"
          >
            {isOpen ? "⌃" : "⌄"}
          </span>
        </div>
      </button>
      {isOpen && (
        <div
          className={`absolute top-full z-[1000] mt-[-1px] w-full overflow-y-auto bg-white ${menuClassName}`}
        >
          <div>
            <input
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  closeDropdown();
                  controlRef.current?.focus();
                }
              }}
              onClick={(event) => event.stopPropagation()}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full rounded border px-3 py-2 text-base"
            />
          </div>
          {actionButton && (
            <button
              type="button"
              className="btn btn-green m-2"
              onClick={() => {
                closeDropdown();
                actionButton.onClick();
              }}
            >
              {actionButton.label}
            </button>
          )}
          <div role="listbox" className="m-h-[400px] overflow-y-auto">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = optionValue === value;

                return (
                  <button
                    key={optionValue}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`block w-full px-3 py-2 text-left text-base text-gray-800 hover:bg-sky-50 ${
                      isSelected ? "bg-sky-50" : ""
                    }`}
                    onClick={() => {
                      onChange(option);
                      closeDropdown();
                    }}
                  >
                    {optionLabel}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-base text-gray-400">
                {noOptionsMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableReactDropdown;
